<script setup lang="ts">
import { useId, type VNode } from 'vue';
import Dialog from 'primevue/dialog';

import UiDialogCloseButton from './UiDialogCloseButton.vue';

defineOptions({ inheritAttrs: false, name: 'UiDialogShell' });

interface Props {
  closeDisabled?: boolean;
  closeLabel?: string;
  closeOnEscape?: boolean;
  description?: string;
  dismissableMask?: boolean;
  illustration?: string;
  labelledBy?: string;
  maxWidth?: string;
  showPattern?: boolean;
  /**
   * Blocking dialogs (a security check the user must answer) omit the close
   * button rather than render a disabled one.
   */
  showClose?: boolean;
  surface?: 'plain' | 'gradient' | 'gradient-border' | 'white';
  surfaceClass?: string;
  testId?: string;
  title?: string;
  width?: string;
}

const {
  closeDisabled = false,
  closeLabel = 'Close',
  closeOnEscape = true,
  description,
  dismissableMask = true,
  illustration,
  labelledBy,
  maxWidth = '92vw',
  showPattern = true,
  showClose = true,
  surface = 'plain',
  surfaceClass,
  testId,
  title,
  width = 'var(--dialog-width-md)',
} = defineProps<Props>();

const visible = defineModel<boolean>('visible', { required: true });
const titleId = useId();

const emit = defineEmits<{
  close: [];
}>();

defineSlots<{
  actions?: () => VNode[];
  body?: () => VNode[];
  illustration?: () => VNode[];
  surface?: () => VNode[];
}>();

const handleVisibilityChange = (nextVisible: boolean) => {
  if (closeDisabled && !nextVisible) {
    return;
  }

  const wasVisible = visible.value;

  visible.value = nextVisible;

  if (wasVisible && !nextVisible) {
    emit('close');
  }
};

const handleClose = () => {
  handleVisibilityChange(false);
};
</script>

<template>
  <Dialog
    v-bind="$attrs"
    :visible="visible"
    modal
    :closable="false"
    :draggable="false"
    :showHeader="false"
    :dismissableMask="dismissableMask && !closeDisabled"
    :closeOnEscape="closeOnEscape && !closeDisabled"
    class="ui-dialog-shell ui-dialog-shell--built-in-container"
    :style="{ width, maxWidth }"
    :aria-labelledby="labelledBy ?? (title ? titleId : undefined)"
    @update:visible="handleVisibilityChange"
  >
    <template #container>
      <div
        class="ui-dialog-shell__surface"
        :class="[
          `ui-dialog-shell__surface--${surface}`,
          { 'ui-dialog-shell__surface--patterned': showPattern },
          { 'ui-dialog-shell__surface--custom-layout': $slots.surface },
          surfaceClass,
        ]"
        :data-testid="testId"
      >
        <slot name="surface">
          <UiDialogCloseButton
            v-if="showClose"
            class="ui-dialog-shell__close"
            :disabled="closeDisabled"
            :label="closeLabel"
            @click="handleClose"
          />

          <div class="ui-dialog-shell__content">
            <slot name="illustration">
              <div v-if="illustration" class="ui-dialog-shell__illustration-frame">
                <img :src="illustration" alt="" class="ui-dialog-shell__illustration" />
              </div>
            </slot>

            <h2 v-if="title" :id="titleId" class="ui-dialog-shell__title">{{ title }}</h2>

            <slot name="body">
              <p v-if="description" class="ui-dialog-shell__description">{{ description }}</p>
            </slot>
          </div>

          <div v-if="$slots.actions" class="ui-dialog-shell__actions">
            <slot name="actions" />
          </div>
        </slot>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:global(.p-dialog.ui-dialog-shell--built-in-container) {
  --p-dialog-background: transparent;
  --p-dialog-border-radius: #{$radius-16};
  --p-dialog-shadow: none;

  border: 0;
}

.ui-dialog-shell {
  &__surface {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-2xl;
    padding: $spacing-2xl;
    border: 1px solid var(--p-surface-100);
    border-radius: var(--p-dialog-border-radius);
    box-shadow: 0 4px 8px 0 $color-base-shadow;
    overflow: hidden;

    &--patterned::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      background: url('../../assets/media/dots-2.svg') top left repeat-x;
      mask-image: linear-gradient(to bottom, black 0%, transparent 60%);
      mask-repeat: no-repeat;
      mask-size: 100% 100%;
      pointer-events: none;
    }

    &--plain {
      border-color: transparent;
      background: $color-bg-elevated;
    }

    &--gradient {
      border-color: color-mix(in srgb, var(--p-surface-300) 75%, transparent);
      background: linear-gradient(180deg, $color-bg-widget 0%, $color-bg-subtle 100%);
    }

    &--gradient-border {
      border-color: transparent;
      background:
        linear-gradient(var(--p-surface-0), var(--p-surface-0)) padding-box,
        linear-gradient(180deg, #f2f2f7 0%, #eaedfe 100%) border-box;
    }

    // Pinned white in both themes, for dialogs that embed third-party content
    // authored for a light background. The chrome inside has to be told which
    // end of each ramp reads against white - the theme cannot do it, because
    // the --p-* aliases resolve once on :root.
    &--white {
      border-color: transparent;
      background: $color-base-white;
      color: $color-text-primary;

      --p-button-text-secondary-hover-background: #{$color-button-ghost-hover};
    }

    &--custom-layout {
      align-items: stretch;
      gap: 0;
      padding: 0;

      > * {
        position: relative;
        z-index: 1;
      }
    }
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-md;
    width: 100%;
  }

  &__title {
    margin: 0;
    color: var(--p-surface-700);
    font-family: var(--font-family-base);
    font-size: $font-size-xl;
    font-weight: $font-weight-semibold;
    line-height: 1;
    letter-spacing: -0.088px;
    text-align: center;
  }

  &__illustration-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 6.25rem;
    overflow: hidden;
    border-radius: $radius-16;
  }

  &__illustration {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__description {
    margin: 0;
    color: $color-text-primary;
    font-family: var(--font-family-base);
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
    line-height: $line-height-xs;
    letter-spacing: -0.056px;
    text-align: center;
  }

  &__actions {
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: $spacing-sm;

    :deep(.p-button) {
      letter-spacing: var(--p-letter-spacing-tight-1);
    }
  }
}

[data-theme='dark'] .ui-dialog-shell__surface--white {
  color: $color-text-inverse;

  --p-button-text-secondary-hover-background: #{$color-base-surface-100};
}

@media (max-width: 640px) {
  .ui-dialog-shell {
    &__surface {
      &:not(.ui-dialog-shell__surface--custom-layout) {
        padding: $spacing-2xl;
      }
    }
  }
}
</style>
